// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps, ModalProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import { modalButtonsActionStyles, xMask } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface PaginationModalProps extends ModalProps {
    setIsMoveToModalOpen: (value: boolean) => void;
    currentPage: number;
    finalPageIndex: number;
    /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
    onChangePage: (page: number) => void;
}

export interface PaginationModalStyles {
    modal?: ModalPresentationProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    moveToField?: TextFieldPresentationProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    footerGridItem?: GridItemProps;
}

const paginationModalStyles: PaginationModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    gridContainer: {
        gap: 30,
    },
    gridItem: {
        width: 12,
    },
    moveToField: {
        labelProps: {
            mergeCss: true,
            css: css`
                width: 62px;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: calc(100% - 62px);
            `,
        },
    },
    footerGridItem: {
        ...modalButtonsActionStyles.buttonsGridItem,
    },
    submitButton: {
        ...modalButtonsActionStyles.submitButton,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
};

const styles = paginationModalStyles;
const PaginationModal: FC<PaginationModalProps> = ({
    isOpen,
    onChangePage,
    setIsMoveToModalOpen,
    currentPage,
    finalPageIndex,
}) => {
    const [moveToField, setMoveToField] = useState("");
    const [moveToFieldError, setMoveToFieldError] = useState("");

    const onModalCloseHandler = () => {
        setIsMoveToModalOpen(false);
        setMoveToField("");
    };

    const moveToFieldValidation = (valueAsString: string) => {
        const value = Number(valueAsString);
        if (!valueAsString || value < 1 || value > finalPageIndex || !Number.isInteger(value)) {
            setMoveToFieldError(translate(`Please Enter your value from 1 to ${finalPageIndex}`));
            return false;
        }
        if (value === currentPage) {
            setMoveToFieldError(translate(`You already on this page`));
            return false;
        }
        setMoveToFieldError("");
        return true;
    };

    const onMoveToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (Number(event.currentTarget.value) > finalPageIndex) {
            return;
        }
        setMoveToField(xMask.qty(event.currentTarget.value));
    };

    const onMoveToBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        moveToFieldValidation(event.currentTarget.value);
    };

    const onUpdatePageNumberHandler = () => {
        if (!moveToFieldValidation(moveToField)) {
            return;
        }
        onModalCloseHandler();
        onChangePage(+moveToField);
    };

    return (
        <Modal isOpen={isOpen} headline={translate("Move To")} handleClose={onModalCloseHandler} {...styles.modal}>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.gridItem}>
                    <Typography size={14} weight="normal">
                        {`${translate("Your current page is")} `}
                        <Typography weight="bold">{currentPage}</Typography>
                        {` ${translate("of ")} `}
                        <Typography>{finalPageIndex}</Typography>
                    </Typography>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <TextField
                        placeholder={translate("Enter your next page #")}
                        label={translate("Move To")}
                        labelPosition="left"
                        value={moveToField}
                        onChange={onMoveToChangeHandler}
                        onBlur={onMoveToBlurHandler}
                        error={moveToFieldError}
                        {...styles.moveToField}
                    />
                </GridItem>
                <GridItem {...styles.footerGridItem}>
                    <Button onClick={onModalCloseHandler} {...styles.cancelButton}>
                        {translate("Cancel")}
                    </Button>
                    <Button onClick={onUpdatePageNumberHandler} {...styles.submitButton}>
                        {translate("Move")}
                    </Button>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default PaginationModal;
